import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  FormControl,
  FormHelperText,
  Stack,
  Menu,
  MenuItem,
  Box,
  InputAdornment,
  debounce,
} from "@mui/material";
import {
  Controller,
  FieldValues,
  Path,
} from "react-hook-form";
import { Input } from "Components/Input";
import { SmText } from "Components/Text";
import { StyledAutocomplete } from "./Autocomplete.styles";
import { useTranslation } from "hooks/useTranslation";
import { OptionType } from "types";
import { DownChevronIcon } from "Assets/icons/chevron.icon";
import { AutocompleteFieldProps } from "types/form";

const Autocomplete = <T extends FieldValues>({
  control,
  name,
  placeholder,
  label,
  options,
  getOptionLabel,
  setValue,
  rules,
  error,
  loading = false,
  onFetchOptions,
  debounceTime = 300,
  requiredlabel = false,
  valueName,
}: AutocompleteFieldProps<T>) => {
  const t = useTranslation();
  const fakeInputRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const firstMenuItemRef = useRef<HTMLLIElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null); // Ref to the menu popover
  const clickingOutside = useRef(false);

  const [focused, setFocused] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchOptions = useCallback(
    debounce((value: string) => {
      if (onFetchOptions) onFetchOptions(value);
    }, debounceTime),
    [onFetchOptions, debounceTime]
  );

  const onClose = () => {
    setFocused(false);
    clickingOutside.current = false;
  };

  const onOpen = () => {
    if (clickingOutside.current) {
      clickingOutside.current = false;
      return;
    }
    setFocused(true);
  };

  const onMenuOpened = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onKeyDownOption = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && firstMenuItemRef.current) {
      event.stopPropagation();
      event.preventDefault();
      firstMenuItemRef.current.click();
    }
  };

  const handleClickOutside = (event: any) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as Node) &&
      !fakeInputRef.current?.contains(event.target as Node)
    ) {
      clickingOutside.current = true;
      setFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <FormControl fullWidth error={!!error}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState }) => {
          return (
            <Stack spacing={1}>
              <Input
                {...field}
                toplabel={label}
                requiredlabel={requiredlabel}
                ref={fakeInputRef}
                onClick={onOpen}
                onFocus={onOpen}
                fullWidth
                value={field?.value?.label || ""}
                placeholder={placeholder}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <DownChevronIcon
                        style={{
                          transform: focused
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />

              {/* Search box */}
              <Menu
                open={focused}
                onClose={onClose}
                anchorEl={fakeInputRef.current}
                slotProps={{
                  paper: {
                    sx: {
                      width: fakeInputRef.current?.clientWidth,
                      padding: "10px 0",
                      "& .MuiList-root": {
                        padding: 0,
                      },
                    },
                    ref: menuRef,
                  },
                }}
                onMouseEnter={onMenuOpened}
                onKeyDown={onKeyDownOption}
              >
                <StyledAutocomplete
                  {...field}
                  open={false}
                  options={options}
                  getOptionLabel={getOptionLabel}
                  loading={loading}
                  onChange={(_, newValue) => {
                    setValue(name, newValue as T[Path<T>], {
                      shouldValidate: true,
                    });
                  }}
                  renderInput={(params) => (
                    <Input
                      {...params}
                      placeholder={t("search")}
                      onChange={(e) => debouncedFetchOptions(e.target.value)}
                      inputRef={inputRef}
                    />
                  )}
                  sx={{
                    px: 1.5,
                    pb: 1.25,
                    "& .MuiAutocomplete-inputRoot": {
                      paddingRight: "12px !important",
                    },
                  }}
                  clearIcon={null}
                  popupIcon={null}
                />
                <Box maxHeight={200} sx={{ overflowY: "auto" }}>
                  {options.map((option, index) => (
                    <MenuItem
                      key={index}
                      onClick={(event) => {
                        event.stopPropagation();
                        setValue(name, option as T[Path<T>], {
                          shouldValidate: true,
                        });
                        if (valueName) {
                          setValue(valueName, option?.value);
                        }
                        clickingOutside.current = true;
                        setFocused(false);
                        fakeInputRef.current?.blur();
                      }}
                      sx={{
                        backgroundColor:
                          index === 0 ? "rgba(0, 0, 0, 0.08)" : "initial",
                      }}
                      ref={index === 0 ? firstMenuItemRef : null}
                    >
                      <SmText className="ellipsis-text">
                        {(option as OptionType).label}
                      </SmText>
                    </MenuItem>
                  ))}
                </Box>
              </Menu>
            </Stack>
          );
        }}
      />
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default Autocomplete;
